import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IPurposeOfTravel } from '@supoui/shared/model/purpose-of-travel';
import { IcdCodes, IcdCodesAdapter } from '@supoui/shared/model/icdCodes';
import { HttpService } from '@supoui/core/services/http.service';
import { NGXLogger } from 'ngx-logger';
import { IApplicationCustomerProfile } from '@supoui/shared/model/application-customer-profile';
import { map } from 'rxjs/operators';
import { CountryAdapter, CountryDetails } from '@supoui/shared/model/country';

@Injectable({
  providedIn: 'root'
})
export class PublicMasterListService {
  private cachedPurposesOfTravel = new BehaviorSubject<IPurposeOfTravel[]>([]);
  private cachedPurposesOfTraveLoading = false;

  private cachedIcd10 = new BehaviorSubject<IcdCodes[]>([]);
  private cachedIcd10Loading = false;

  private cachedCustomerProfile = new BehaviorSubject<IApplicationCustomerProfile[]>([]);
  private cachedCustomerProfileLoading = false;

  constructor(
    private httpService: HttpService,
    private icdCodesAdapter: IcdCodesAdapter,
    private countryAdapter: CountryAdapter,
    private logger: NGXLogger
  ) {}

  getPurposesOfTravel(): Observable<IPurposeOfTravel[]> {
    if (!this.cachedPurposesOfTravel.value.length && !this.cachedPurposesOfTraveLoading) {
      this.cachedPurposesOfTraveLoading = true;
      this.fetchPurposesOfTravel().subscribe(
        result => {
          this.cachedPurposesOfTravel.next(result);
          this.cachedPurposesOfTraveLoading = false;
          return this.cachedPurposesOfTravel.asObservable();
        },
        error => this.logger.error(error)
      );
    }
    return this.cachedPurposesOfTravel.asObservable();
  }

  private fetchPurposesOfTravel(): Observable<IPurposeOfTravel[]> {
    return this.httpService.get<IPurposeOfTravel[]>('services/supo-ui/api/public-master-list/purposes-of-travel');
  }

  public getIcdCodes(): Observable<IcdCodes[]> {
    if (!this.cachedIcd10.value.length && !this.cachedIcd10Loading) {
      this.cachedIcd10Loading = true;
      this.fetchIcdCodes().subscribe(
        result => {
          this.cachedIcd10.next(result);
          this.cachedIcd10Loading = false;
          return this.cachedIcd10.asObservable();
        },
        error => this.logger.error(error)
      );
    }
    return this.cachedIcd10.asObservable();
  }

  private fetchIcdCodes(): Observable<IcdCodes[]> {
    return this.httpService
      .get<IcdCodes[]>('services/supo-ui/api/public-master-list/icd10')
      .pipe(map((data: IcdCodes[]) => data.map(item => this.icdCodesAdapter.adapt(item))));
  }

  public getApplicationCustomerProfiles(): Observable<IApplicationCustomerProfile[]> {
    if (!this.cachedCustomerProfile.value.length && !this.cachedCustomerProfileLoading) {
      this.cachedCustomerProfileLoading = true;
      this.fetchCustomerProfiles().subscribe(
        result => {
          this.cachedCustomerProfile.next(result);
          this.cachedCustomerProfileLoading = false;
          return this.cachedCustomerProfile.asObservable();
        },
        error => this.logger.error(error)
      );
    }
    return this.cachedCustomerProfile.asObservable();
  }

  private fetchCustomerProfiles(): Observable<IApplicationCustomerProfile[]> {
    return this.httpService.get<IApplicationCustomerProfile[]>('services/supo-ui/api/public-master-list/customer-profiles');
  }

  public searchCountries(query: string): Observable<CountryDetails[]> {
    return this.httpService
      .get<any[]>(`services/supo-ui/api/public-master-list/countries/search/${query}`)
      .pipe(map((data: any[]) => data.map(item => this.countryAdapter.adaptToCountryDetails(item))));
  }

  public getCountryByAlpha2(query: string): Observable<CountryDetails> {
    return this.httpService
      .get<CountryDetails>(`services/supo-ui/api/public-master-list/countries/searchByAlpha2/${query}`)
      .pipe(map(item => this.countryAdapter.adaptToCountryDetails(item)));
  }
}
